"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.secureLocalStorage = exports.secureSessionStorage = void 0;
/**
 * secureSessionStorage & secureLocalStorage
 *
 * AES-256-GCM wrappers around sessionStorage/localStorage for at-rest PII
 * protection. Wire format matches unite-v2-ui / CRM / test-buddy-ui so
 * encrypted values are interoperable across those apps.
 *
 * Wire format on disk:
 *   { "data": <base64 ciphertext+tag>, "iv": <base64 12B>,
 *     "algorithm": "AES-256-GCM", "_encrypted": true }
 *
 * Key source: process.env.CRM_ENCRYPTION_KEY, inlined at build time by
 * webpack's DefinePlugin (see webpack.config.babel.js) — the browser bundle
 * has no process.env at runtime, so the key must be baked in per-build.
 *
 * Backward compatibility:
 *   - getItem(): if stored value isn't encrypted-shape, returned as-is (legacy plaintext).
 *   - setItem(): always encrypts when ready; soft-falls to plaintext on error.
 *
 * Side effects on import (so any host app gets this by only loading the
 * built bundle — no host-app code required):
 *   - Patches Storage.prototype.getItem/setItem/removeItem so plain,
 *     pre-existing `localStorage.getItem(...)` / `sessionStorage.setItem(...)`
 *     calls anywhere in the host app are transparently encrypted too, with no
 *     code changes required in the host app.
 *   - Replaces window.localStorage/sessionStorage with a Proxy so property-style
 *     access (`localStorage.userId`, `localStorage['userId']`, `delete
 *     localStorage.userId`, `'userId' in localStorage`) is also covered.
 *     Browsers implement this named-property access as exotic [[Get]]/[[Set]]
 *     behavior directly on the Storage object, which never calls through
 *     Storage.prototype — so the getItem/setItem patch above cannot see it.
 *   - Does NOT set window.secureSessionStorage / window.secureLocalStorage —
 *     this module never publishes or reads those; nothing here creates
 *     instances of them.
 */
const forge = __importStar(require("node-forge"));
const ALGORITHM = 'AES-GCM';
const WIRE_ALGORITHM = 'AES-256-GCM';
const IV_LENGTH = 12; // bytes
const TAG_LENGTH = 128; // bits
const GCM_TAG_BYTES = 16; // bytes
let _key = null;
// Set to true ONLY when init() validates the key (forge loaded + key present +
// exactly 32 bytes). Stays false if the build-time env var is missing/wrong —
// causing silent plaintext fallback instead of app breakage.
let _ready = false;
// 'token' / 'X-CSRF-Token' must never be encrypted — request headers built
// elsewhere in this repo (buyback.ts, employee.ts, orderdashboard.ts,
// technician.ts) read these keys straight out of localStorage/sessionStorage
// and send them as-is; an encrypted-shape blob there would break auth on
// every request. Always in plaintextKeys regardless of host page config.
const ALWAYS_PLAINTEXT_KEYS = ['token', 'X-CSRF-Token'];
// Keys the host page opts out of encryption for, via
// <script plaintext-keys="theme,locale"> on the tag loading this bundle —
// same host->bundle config convention as logs/errorLogs/logs.js's
// mailto/logs-for attributes. Everything not listed keeps encrypting.
let plaintextKeys = new Set(ALWAYS_PLAINTEXT_KEYS);
function readKeyConfigFromScriptTags() {
    if (typeof document === 'undefined')
        return;
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const attr = scripts[i].getAttribute('plaintext-keys');
        if (attr) {
            const hostKeys = attr.split(',').map((k) => k.trim()).filter(Boolean);
            // Union, not replace — a host page's own plaintext-keys list must not
            // be able to drop token/X-CSRF-Token back into encryption.
            plaintextKeys = new Set([...ALWAYS_PLAINTEXT_KEYS, ...hostKeys]);
        }
    }
}
function shouldEncrypt(key) {
    return !plaintextKeys.has(key);
}
function init() {
    try {
        let b64 = process.env.CRM_ENCRYPTION_KEY;
        if (b64 && b64.indexOf('{') === 0) {
            try {
                b64 = JSON.parse(b64).CRM_ENCRYPTION_KEY || b64;
            }
            catch (e) { /* leave as-is */ }
        }
        if (!b64) {
            console.warn('[secureStorage] No encryption key — plaintext mode.');
            return false;
        }
        const raw = forge.util.decode64(b64);
        if (raw.length !== 32) {
            console.error('[secureStorage] Key must be 32 bytes for AES-256. Got: ' + raw.length);
            return false;
        }
        _key = raw;
        _ready = true;
        return true;
    }
    catch (e) {
        console.error('[secureStorage] init failed:', e);
        return false;
    }
}
function encrypt(plaintextStr) {
    const iv = forge.random.getBytesSync(IV_LENGTH);
    const cipher = forge.cipher.createCipher(ALGORITHM, _key);
    cipher.start({ iv, tagLength: TAG_LENGTH });
    cipher.update(forge.util.createBuffer(plaintextStr, 'utf8'));
    cipher.finish();
    // Web Crypto convention: 16-byte GCM tag appended to ciphertext
    const ctWithTag = cipher.output.getBytes() + cipher.mode.tag.getBytes();
    return {
        data: forge.util.encode64(ctWithTag),
        iv: forge.util.encode64(iv),
        algorithm: WIRE_ALGORITHM,
        _encrypted: true,
    };
}
function decrypt(payload) {
    const ivBytes = forge.util.decode64(payload.iv);
    const ctWithTag = forge.util.decode64(payload.data);
    if (ctWithTag.length < GCM_TAG_BYTES)
        return null;
    const ct = ctWithTag.substring(0, ctWithTag.length - GCM_TAG_BYTES);
    const tag = ctWithTag.substring(ctWithTag.length - GCM_TAG_BYTES);
    const decipher = forge.cipher.createDecipher(ALGORITHM, _key);
    decipher.start({
        iv: ivBytes,
        tag: forge.util.createBuffer(tag),
        tagLength: TAG_LENGTH,
    });
    decipher.update(forge.util.createBuffer(ct));
    if (!decipher.finish())
        return null;
    return forge.util.decodeUtf8(decipher.output.getBytes());
}
function isEncryptedShape(v) {
    const payload = v;
    return (!!payload &&
        typeof payload === 'object' &&
        typeof payload.data === 'string' &&
        typeof payload.iv === 'string' &&
        (payload.algorithm === WIRE_ALGORITHM || payload._encrypted === true));
}
// Factory: builds a wrapper bound to a given Web Storage (sessionStorage or
// localStorage). _key/_ready are shared across both wrappers — one key, two
// backing stores. Reads/writes go through `native` (captured before
// Storage.prototype is patched below) instead of backingStorage.getItem(...)
// directly — calling the instance method after patching would recurse into
// the patched Storage.prototype.getItem forever.
function createWrapper(backingStorage, native, label) {
    function getItem(key) {
        const stored = native.getItem.call(backingStorage, key);
        if (stored === null)
            return null;
        let parsed;
        try {
            parsed = JSON.parse(stored);
        }
        catch (e) {
            // Not JSON — legacy plaintext string
            return stored;
        }
        if (isEncryptedShape(parsed)) {
            if (!_ready) {
                // guard: can't decrypt without the key — return null rather than expose raw ciphertext
                console.warn(`[${label}] Encrypted blob found but service not ready.`);
                return null;
            }
            try {
                return decrypt(parsed);
            }
            catch (e) {
                console.error(`[${label}] decrypt failed:`, e);
                return null;
            }
        }
        // Parsed JSON but not encrypted shape — legacy plaintext that was JSON-stringified.
        // Return the original stored string so callers' JSON.parse(...) still works.
        return stored;
    }
    function setItem(key, value) {
        // Match native Storage.setItem: always coerce value to string before
        // persisting, so encrypted writes and plaintext-fallback writes produce
        // the same shape.
        const stringValue = String(value);
        if (!shouldEncrypt(key)) {
            native.setItem.call(backingStorage, key, stringValue);
            return;
        }
        try {
            if (_ready) {
                const encrypted = encrypt(stringValue);
                native.setItem.call(backingStorage, key, JSON.stringify(encrypted));
                return;
            }
        }
        catch (e) {
            console.error(`[${label}] encrypt failed, falling back to plaintext:`, e);
        }
        // _ready=false (key missing at build time) — write plaintext, app works as before.
        native.setItem.call(backingStorage, key, stringValue);
    }
    function removeItem(key) {
        native.removeItem.call(backingStorage, key);
    }
    function isReady() {
        return _ready;
    }
    return { getItem, setItem, removeItem, isReady };
}
readKeyConfigFromScriptTags();
init();
// Passthrough to native storage, defensive per-call — used when this module
// can't even get a handle on window.sessionStorage/localStorage (e.g. some
// locked-down/private-browsing modes throw just on property access). Keeps
// behavior identical to "no secureStorage at all" instead of going silent.
function nativePassthrough(getStorage, label) {
    return {
        getItem: (key) => {
            try {
                return getStorage().getItem(key);
            }
            catch (e) {
                console.error(`[${label}] native storage getItem failed:`, e);
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                getStorage().setItem(key, String(value));
            }
            catch (e) {
                console.error(`[${label}] native storage setItem failed:`, e);
            }
        },
        removeItem: (key) => {
            try {
                getStorage().removeItem(key);
            }
            catch (e) {
                console.error(`[${label}] native storage removeItem failed:`, e);
            }
        },
        isReady: () => false,
    };
}
// Names that must keep native Storage semantics under property access instead
// of being treated as a stored key — real methods/attributes plus the Object
// baseline members that are reachable via the prototype chain on any object.
const RESERVED_STORAGE_PROPS = new Set([
    'getItem', 'setItem', 'removeItem', 'clear', 'key', 'length',
    'constructor', 'toString', 'valueOf', 'toLocaleString',
    'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable',
]);
// Wraps a real Storage object so `storage.foo` / `storage['foo']` / `storage.foo = x`
// / `delete storage.foo` / `'foo' in storage` route through the same wrapper as
// getItem/setItem/removeItem, instead of hitting the underlying store directly
// (which is what browsers do for named property access on Storage — it bypasses
// Storage.prototype entirely). getItem/setItem/removeItem/clear/key are returned
// as closures bound to `wrapper`/`target` rather than the patched prototype
// methods, so behavior here doesn't depend on `this` identity at the call site.
function createStorageProxy(target, wrapper) {
    return new Proxy(target, {
        get(t, prop, receiver) {
            if (typeof prop === 'symbol')
                return Reflect.get(t, prop, receiver);
            switch (prop) {
                case 'getItem': return (key) => wrapper.getItem(key);
                case 'setItem': return (key, value) => wrapper.setItem(key, String(value));
                case 'removeItem': return (key) => wrapper.removeItem(key);
                case 'clear': return () => t.clear();
                case 'key': return (index) => t.key(index);
            }
            if (RESERVED_STORAGE_PROPS.has(prop))
                return Reflect.get(t, prop, receiver);
            const value = wrapper.getItem(prop);
            // Native dot/bracket access returns undefined for a missing key, unlike getItem's null.
            return value === null ? undefined : value;
        },
        set(t, prop, value) {
            if (typeof prop === 'symbol' || RESERVED_STORAGE_PROPS.has(prop)) {
                return Reflect.set(t, prop, value);
            }
            wrapper.setItem(prop, value);
            return true;
        },
        deleteProperty(t, prop) {
            if (typeof prop === 'symbol' || RESERVED_STORAGE_PROPS.has(prop)) {
                return Reflect.deleteProperty(t, prop);
            }
            wrapper.removeItem(prop);
            return true;
        },
        has(t, prop) {
            if (typeof prop === 'symbol' || RESERVED_STORAGE_PROPS.has(prop)) {
                return Reflect.has(t, prop);
            }
            return wrapper.getItem(prop) !== null;
        },
    });
}
const noopWrapper = {
    getItem: () => null,
    setItem: () => { },
    removeItem: () => { },
    isReady: () => false,
};
let sessionWrapper = noopWrapper;
let localWrapper = noopWrapper;
// Anything below is best-effort: if constructing the encrypted wrappers or
// patching Storage.prototype throws for any reason, fall through without
// patching Storage.prototype — every host app's existing
// localStorage/sessionStorage calls keep working exactly as before this
// module existed.
if (typeof window !== 'undefined' && typeof Storage !== 'undefined') {
    if (window.__oaSecureStoragePatched) {
        // Already installed by an earlier-loaded oa-commons-ui bundle (api.js/
        // dml.js/logs.js each carry their own copy of this module — no shared
        // webpack chunk between them). Storage.prototype and window.localStorage/
        // sessionStorage are page-global, so that install already covers this
        // bundle's own storage calls too — skip re-patching to avoid nesting a
        // second wrapper layer around the first.
    }
    else {
        try {
            // Capture BEFORE patching — createWrapper's reads/writes use these, and
            // the patch below replaces the instance-visible methods with wrappers
            // that call back into createWrapper's closures.
            const native = {
                getItem: Storage.prototype.getItem,
                setItem: Storage.prototype.setItem,
                removeItem: Storage.prototype.removeItem,
            };
            sessionWrapper = createWrapper(window.sessionStorage, native, 'secureSessionStorage');
            localWrapper = createWrapper(window.localStorage, native, 'secureLocalStorage');
            window.__oaSecureStoragePatched = true;
            // Transparently covers every existing `localStorage.getItem(...)` /
            // `sessionStorage.setItem(...)` call site in the host app — no host-app
            // code changes needed. Dispatch by instance identity since both storages
            // share the same Storage.prototype.
            Storage.prototype.getItem = function (key) {
                const wrapper = this === window.localStorage ? localWrapper : sessionWrapper;
                return wrapper.getItem(key);
            };
            Storage.prototype.setItem = function (key, value) {
                const wrapper = this === window.localStorage ? localWrapper : sessionWrapper;
                wrapper.setItem(key, value);
            };
            Storage.prototype.removeItem = function (key) {
                const wrapper = this === window.localStorage ? localWrapper : sessionWrapper;
                wrapper.removeItem(key);
            };
        }
        catch (e) {
            console.error('[secureStorage] setup failed, falling back to native storage:', e);
            sessionWrapper = nativePassthrough(() => window.sessionStorage, 'secureSessionStorage');
            localWrapper = nativePassthrough(() => window.localStorage, 'secureLocalStorage');
            // Deliberately leave window.__oaSecureStoragePatched unset here — the
            // patch didn't actually take, so a later-loading bundle should still be
            // allowed to attempt its own install rather than treating this as done.
        }
        // Separate, best-effort try: covers `storage.foo` / `storage['foo']` property-style
        // access, which the Storage.prototype patch above cannot see (see file header).
        // Kept independent so that if redefining window.localStorage/sessionStorage fails
        // (e.g. a host page already froze them), the getItem/setItem coverage above still holds.
        try {
            const localProxy = createStorageProxy(window.localStorage, localWrapper);
            const sessionProxy = createStorageProxy(window.sessionStorage, sessionWrapper);
            Object.defineProperty(window, 'localStorage', {
                configurable: true,
                enumerable: true,
                get: () => localProxy,
            });
            Object.defineProperty(window, 'sessionStorage', {
                configurable: true,
                enumerable: true,
                get: () => sessionProxy,
            });
        }
        catch (e) {
            console.error('[secureStorage] property-access proxy install failed — dot/bracket storage access will bypass encryption:', e);
        }
    }
}
exports.secureSessionStorage = sessionWrapper;
exports.secureLocalStorage = localWrapper;
exports.default = { secureSessionStorage: exports.secureSessionStorage, secureLocalStorage: exports.secureLocalStorage };
